import type { LngLatRing } from "./geo.js";

/**
 * Turns coastline rings into the dot field the globe draws its continents
 * from.
 *
 * The customer support portal's `GlobePacketCanvas` does the same job with
 * ~25 hand-typed polygons standing in for the world. We have the real thing
 * vendored already (`/data/ne_110m_land.geojson`, Natural Earth 110m, public
 * domain — see `public/data/README.md`), so the dots land on real coastlines:
 * the Gulf of Bothnia, the Red Sea, the Bay of Bengal and the Great
 * Australian Bight all show up, where the reference's approximation loses
 * them. Same design, better source data.
 *
 * The dots are computed ONCE per land file, not per frame — a frame just
 * rotates and projects the result.
 */

/** A ring plus its bounding box, so the point test can reject in four
 * comparisons instead of walking a few hundred vertices. Natural Earth 110m
 * is ~130 rings totalling ~10 000 points; without this, sampling the grid
 * below costs tens of millions of segment tests and is visible as a stall on
 * the sign-in's critical path. With it, it is a couple of milliseconds. */
interface BoundedRing {
  ring: LngLatRing;
  minLng: number;
  maxLng: number;
  minLat: number;
  maxLat: number;
}

export type LandMask = ReadonlyArray<BoundedRing>;

/** A sampled land point, in degrees. */
export interface LandDot {
  lat: number;
  lng: number;
}

export function prepareLandMask(rings: ReadonlyArray<LngLatRing>): LandMask {
  const out: BoundedRing[] = [];
  for (const ring of rings) {
    if (ring.length < 8) continue;
    let minLng = Infinity;
    let maxLng = -Infinity;
    let minLat = Infinity;
    let maxLat = -Infinity;
    for (let i = 0; i < ring.length; i += 2) {
      const lng = ring[i]!;
      const lat = ring[i + 1]!;
      if (lng < minLng) minLng = lng;
      if (lng > maxLng) maxLng = lng;
      if (lat < minLat) minLat = lat;
      if (lat > maxLat) maxLat = lat;
    }
    out.push({ ring, minLng, maxLng, minLat, maxLat });
  }
  return out;
}

/**
 * Even-odd crossing test against one ring.
 *
 * Even-odd (rather than winding) is deliberate and is what makes holes work
 * for free: Natural Earth encodes the Caspian Sea and Africa's great lakes as
 * inner rings of their landmass, and a point inside one crosses both the
 * outer ring and the inner ring, so it flips back to "sea". That is the whole
 * hole-handling story — there is no separate case for it below.
 */
export function pointInRing(lng: number, lat: number, ring: LngLatRing): boolean {
  let inside = false;
  const n = ring.length;
  for (let i = 0, j = n - 2; i < n; j = i, i += 2) {
    const xi = ring[i]!;
    const yi = ring[i + 1]!;
    const xj = ring[j]!;
    const yj = ring[j + 1]!;
    // Half-open in y: a vertex exactly on the ray is counted once, not twice.
    if (yi > lat !== yj > lat) {
      const t = (lat - yi) / (yj - yi);
      if (lng < xi + t * (xj - xi)) inside = !inside;
    }
  }
  return inside;
}

/** Is this coordinate on land? */
export function isLand(lng: number, lat: number, mask: LandMask): boolean {
  let inside = false;
  for (const b of mask) {
    if (lng < b.minLng || lng > b.maxLng || lat < b.minLat || lat > b.maxLat) continue;
    if (pointInRing(lng, lat, b.ring)) inside = !inside;
  }
  return inside;
}

/** Grid geometry. The reference samples every 4° between 56°S and 78°N, and
 * offsets alternate rows by half a step so the field reads as a stipple
 * rather than as graph paper. Both are copied on purpose: the spacing is what
 * gives the globe its density, and the offset is what stops the dots from
 * lining up into visible columns as the planet turns. */
export const DOT_STEP_DEG = 4;
export const DOT_LAT_MIN = -56;
export const DOT_LAT_MAX = 78;

/**
 * Walk the grid and keep the points that are on land.
 *
 * Latitude is capped short of the poles for the same reason the reference
 * caps it: an equal-degree grid crowds toward the poles, so Antarctica and
 * the high Arctic would otherwise render as two solid caps of overlapping
 * dots. 78°N still keeps Svalbard and the top of Greenland.
 */
export function sampleLandDots(mask: LandMask, step: number = DOT_STEP_DEG): LandDot[] {
  const dots: LandDot[] = [];
  if (!(step > 0)) return dots;
  let row = 0;
  for (let lat = DOT_LAT_MIN; lat <= DOT_LAT_MAX; lat += step, row += 1) {
    const offset = row % 2 === 0 ? 0 : step / 2;
    for (let lng = -180 + offset; lng <= 180; lng += step) {
      if (isLand(lng, lat, mask)) dots.push({ lat, lng });
    }
  }
  return dots;
}
